import {
  AfterViewInit,
  Component,
  DestroyRef,
  ErrorHandler,
  forwardRef,
  Input,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { isSentryErrorHandler } from '@techniek-team/sentry-web';
import {
  BehaviorSubject,
  firstValueFrom,
  Observable,
  ReplaySubject,
} from 'rxjs';
import { delay, filter, map } from 'rxjs/operators';
import { AlfredClientConfig } from '../../../../alfred-client.config';
import { AlfredClientService } from '../../../../alfred-client.service';
import { ScaleQuestionAnswer } from '../../../../models/answer/scale-answer.model';
import { IntRange } from '../../../../models/int-range/int-range';
import { StarQuestion } from '../../../../models/questions/star-question.model';
import { OnLoaded } from '@lyceo/shared/dynamic-component-loading/feature-main';
import { InvalidAnswerTypeError } from '../../../../shared/errors/invalid-answer-type-error';
import { outOfRangeValidator } from '../../../../shared/validators/out-of-range.validator';
import {
  QuestionAnswer,
  QuestionControlType,
} from '../question-type.interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { QuestionDisplayComponent } from '../../question-display/question-display.component';
import { StarControlComponent } from './star-control/star-control.component';
import { ScaleQuestionLabelsComponent } from './scale-question-labels/scale-question-labels.component';
import { AsyncPipe } from '@angular/common';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-star-question',
  templateUrl: './star-question.component.html',
  styleUrls: ['./star-question.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StarQuestionComponent),
      multi: true,
    },
  ],
  imports: [
    QuestionDisplayComponent,
    StarControlComponent,
    ReactiveFormsModule,
    ScaleQuestionLabelsComponent,
    AsyncPipe,
  ],
})
export class StarQuestionComponent
  implements OnInit, AfterViewInit, OnLoaded, QuestionControlType
{
  private readonly errorHandler: ErrorHandler = inject(ErrorHandler);
  private readonly config: AlfredClientConfig = inject(AlfredClientConfig);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly alfredClientService: AlfredClientService =
    inject(AlfredClientService);

  /**
   * questions data used to created the gap match question.
   * private Used in inner html template.
   */
  public _question$: BehaviorSubject<StarQuestion> =
    new BehaviorSubject<StarQuestion>(undefined as never);

  /**
   * Map the incoming IntRangeModel to an usable IntRange class.
   */
  public set question(question: StarQuestion) {
    if (!question.scaleRange && this.alfredClientService.isDirectHandleMode()) {
      question.scaleRange = new IntRange(0, 5, true, true);
    }

    if (
      !question.scaleQuestionLabels &&
      this.alfredClientService.isDirectHandleMode()
    ) {
      question.scaleQuestionLabels = [];
    }
    if (Array.isArray(question.scaleQuestionLabels)) {
      question.scaleQuestionLabels = question.scaleQuestionLabels.sort(
        (a, b) => a.onScalePoint - b.onScalePoint,
      );
    }

    this._question$.next(question);
  }

  public get question$(): Observable<StarQuestion> {
    return this._question$.pipe(filter((question) => !!question));
  }

  /**
   * Input to prefill the answer of the question.
   */
  public set answer(answer: ScaleQuestionAnswer) {
    // because the change detection of the starCtrl is base on a behavior subject
    // the valueChange will emit twice. One because we set they value here and the second
    // time trigger when the behavior subject in triggered. To prevent this we
    // don't emit a event on set value
    if (!answer) {
      this.starCtrl.setValue(null, { emitEvent: false });
      return;
    }

    if (typeof answer === 'number') {
      this.starCtrl.setValue(this.transformGivenRatingToScaleRange(answer), {
        emitEvent: false,
      });
      return;
    }

    if (this.alfredClientService.isDirectHandleMode()) {
      return;
    }

    // This form control only accepts number or null. Other enums are ignored
    // and a silence error is send to sentry
    if (!this.config.production) {
      throw new InvalidAnswerTypeError(answer, 'number');
    }
    if (isSentryErrorHandler(this.errorHandler)) {
      const error: InvalidAnswerTypeError = new InvalidAnswerTypeError(
        answer,
        'number',
      );
      this.errorHandler.captureError(error.message);
    }
  }

  public get answer(): ScaleQuestionAnswer {
    return this.getValue() as ScaleQuestionAnswer;
  }

  /**
   * The form control lib-star-control component.
   */
  public starCtrl: UntypedFormControl = new UntypedFormControl(null, [
    Validators.required,
  ]);

  /**
   * Subject which starts to emit when the view is rendered. This is needed because the
   * component is dynamically loading.
   */
  public onFinishedInit$: ReplaySubject<void> = new ReplaySubject<void>();

  /**
   * @inheritDoc
   */
  public ngOnInit(): void {
    this.setStarCtrlValidatorsObserver();
  }

  /**
   * @inheritDoc
   */
  public ngAfterViewInit(): void {
    this.onFinishedInit$.next();
    this.onFinishedInit$.complete();
  }

  /**
   * @inheritDoc
   */
  public ready(): Promise<void> {
    return firstValueFrom(this.onFinishedInit$.pipe(delay(20)));
  }

  /**
   * @inheritDoc
   */
  public isPristine(): boolean {
    return this.starCtrl.pristine;
  }

  /**
   * @inheritDoc
   */
  public getValue(): QuestionAnswer {
    return this.deTransformGivenRatingToScaleRange(this.starCtrl.value);
  }

  /**
   * @inheritDoc
   */
  public onValueChanges(): Observable<QuestionAnswer> {
    return this.starCtrl.valueChanges.pipe(
      map((value) => this.deTransformGivenRatingToScaleRange(value)),
    );
  }

  /**
   * @inheritDoc
   */
  public isValid(): boolean {
    return this.starCtrl.valid;
  }

  /**
   * @inheritDoc
   */
  public onValidChanges(): Observable<boolean> {
    return this.starCtrl.statusChanges.pipe(
      filter((status) => status === 'VALID' || status === 'INVALID'),
      map((status) => status === 'VALID'),
    );
  }

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.starCtrl.disable();
    } else {
      this.starCtrl.enable();
    }
  }

  /**
   * Converts the given rating back to the given scale range.
   * Subtracting the scale range start by the given rating.
   *
   * example/proof:
   * :range: === :items:   === :item_count: < given > :min_item: === :calculation:
   * [0-5] === 0 1 2 3 4 5 === 6            <  4    > 0          === 4 - (0 -1 = -1) = 5
   * [0-5) === 0 1 2 3 4   === 5            <  4    > 0          === 4 - (0 -1 = -1) = 5
   * (0-5] === 1 2 3 4 5   === 5            <  4    > 1          === 4 - (1 -1 = 0) = 4
   * (0-5) === 1 2 3 4     === 4            <  4    > 1          === 4 - (1 -1 = 0) = 4
   *
   * [1-6] === 1 2 3 4 5 6 === 6            <  4    > 1          === 4 - (1 -1 = 0) = 4
   * [1-6) === 1 2 3 4 5   === 5            <  4    > 1          === 4 - (1 -1 = 0) = 4
   * (1-6] === 2 3 4 5 6   === 5            <  4    > 2          === 4 - (2 -1 = 1) = 5
   * (1-6) === 2 3 4 5     === 4            <  4    > 2          === 4 - (2 -1 = 1) = 5
   */
  private transformGivenRatingToScaleRange(
    rating: ScaleQuestionAnswer,
  ): number {
    const scaleRange: IntRange = this._question$.getValue().scaleRange;
    return rating - (scaleRange.min - 1);
  }

  /**
   * Converts the given rating back to the given scale range.
   * Adding the scale range start by the given rating.
   *
   * example/proof:
   * :range: === :items:   === :item_count: < given > :min_item: === :calculation:
   * [0-5] === 0 1 2 3 4 5 === 6            <  4    > 0          === 4 + (0 -1 = -1) = 3
   * [0-5) === 0 1 2 3 4   === 5            <  4    > 0          === 4 + (0 -1 = -1) = 3
   * (0-5] === 1 2 3 4 5   === 5            <  4    > 1          === 4 + (1 -1 = 0) = 4
   * (0-5) === 1 2 3 4     === 4            <  4    > 1          === 4 + (1 -1 = 0) = 4
   *
   * [1-6] === 1 2 3 4 5 6 === 6            <  4    > 1          === 4 + (1 -1 = 0) = 4
   * [1-6) === 1 2 3 4 5   === 5            <  4    > 1          === 4 + (1 -1 = 0) = 4
   * (1-6] === 2 3 4 5 6   === 5            <  4    > 2          === 4 + (2 -1 = 1) = 5
   * (1-6) === 2 3 4 5     === 4            <  4    > 2          === 4 + (2 -1 = 1) = 5
   */
  private deTransformGivenRatingToScaleRange(rating: number): number {
    const scaleRange: IntRange = this._question$.getValue().scaleRange;
    if (!rating) {
      return rating;
    }
    return rating + (scaleRange.min - 1);
  }

  /**
   * Set the correct validator on star control
   */
  private setStarCtrlValidatorsObserver(): void {
    this._question$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((question) => !!question && !!question.scaleRange),
      )
      .subscribe((question) => {
        this.starCtrl.setValidators([
          Validators.required,
          outOfRangeValidator(question.scaleRange, (rating) => {
            return rating + (question.scaleRange.min - 1);
          }),
        ]);
      });
  }
}
